import React, { useRef, useEffect, useState } from 'react';

const ROLES = [null, 'product', 'style', 'context'];

const ROLE_CONFIG = {
  product: { label: 'Product', short: 'P', bg: 'rgba(52,211,153,0.92)', border: 'rgba(52,211,153,0.55)', title: 'Product — will be passed to image generation' },
  style:   { label: 'Style',   short: 'S', bg: 'rgba(167,139,250,0.92)', border: 'rgba(167,139,250,0.55)', title: 'Style reference — used for visual inspiration' },
  context: { label: 'Context', short: 'C', bg: 'rgba(156,163,175,0.88)', border: 'rgba(156,163,175,0.45)', title: 'Context only — AI reads it but won\'t pass to tools' },
};

export default function ChatInput({
  input,
  onInputChange,
  onSubmit,
  onStop,
  onQueueMessage,
  isStreaming,
  disabled,
  images = [],
  onImagesChange,
  openAssetPicker,
  onOpenPresets,
  isMobile = false,
  queuedMessage = null,
  onClearQueue,
}) {
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [focused, setFocused] = useState(false);

  // Auto-resize textarea
  useEffect(() => {
    const el = textareaRef.current;
    if (el) {
      el.style.height = 'auto';
      el.style.height = Math.min(el.scrollHeight, 160) + 'px';
    }
  }, [input]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (canQueue) {
        onQueueMessage(input.trim());
      } else if (canSubmit) {
        onSubmit(e);
      }
    }
    // Cmd/Ctrl+K opens presets
    if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
      e.preventDefault();
      onOpenPresets?.();
    }
  };

  const handleFiles = (files) => {
    if (!onImagesChange) return;
    const validFiles = Array.from(files).filter(
      f => f.type.startsWith('image/') && f.size <= 10 * 1024 * 1024
    );
    if (validFiles.length === 0) return;

    let loadedCount = 0;
    const newImages = [];

    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        newImages.push({ url: e.target.result, name: file.name, file, role: null });
        loadedCount++;
        if (loadedCount === validFiles.length) {
          onImagesChange(prev => [...prev, ...newImages]);
        }
      };
      reader.onerror = () => {
        loadedCount++;
        if (loadedCount === validFiles.length && newImages.length > 0) {
          onImagesChange(prev => [...prev, ...newImages]);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const imageFiles = [];
    for (const item of items) {
      if (item.type.startsWith('image/')) imageFiles.push(item.getAsFile());
    }
    if (imageFiles.length > 0) {
      e.preventDefault();
      handleFiles(imageFiles);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  const removeImage = (index) => {
    if (onImagesChange) onImagesChange(prev => prev.filter((_, i) => i !== index));
  };

  const cycleRole = (index) => {
    if (!onImagesChange) return;
    onImagesChange(prev => prev.map((img, i) => {
      if (i !== index) return img;
      const currentIdx = ROLES.indexOf(img.role ?? null);
      const nextRole = ROLES[(currentIdx + 1) % ROLES.length];
      return { ...img, role: nextRole };
    }));
  };

  const hasInput = !!(input?.trim() || images.length > 0);
  const canSubmit = !disabled && !isStreaming && hasInput;
  const canStop = isStreaming && onStop && !input?.trim();
  const canQueue = isStreaming && onQueueMessage && !!input?.trim();

  const handleAssetsConfirm = (selectedAssets) => {
    if (!onImagesChange || !selectedAssets?.length) return;
    const newImgs = selectedAssets.map(a => ({
      url: a.url,
      name: a.name || a.asset_type || 'asset',
      asset_id: a.asset_id,
      role: null,
    }));
    onImagesChange(prev => [...prev, ...newImgs]);
  };

  const attachActive = !disabled && !isStreaming;

  return (
    <>
      {/* Queued message preview */}
      {queuedMessage && (
        <div style={{
          margin: isMobile ? '0 12px 4px' : '0 20px 4px', padding: '6px 12px',
          borderRadius: 10, display: 'flex', alignItems: 'center', gap: 8,
          background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.25)',
          fontSize: 11, color: 'rgba(139,92,246,0.8)',
        }}>
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <polyline points="17 1 21 5 17 9" />
            <path d="M3 11V9a4 4 0 0 1 4-4h14" />
          </svg>
          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            Queued: {queuedMessage}
          </span>
          <button
            type="button"
            onClick={onClearQueue}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'rgba(139,92,246,0.6)', fontSize: 14, padding: '0 2px', lineHeight: 1,
            }}
          >×</button>
        </div>
      )}
      <form onSubmit={onSubmit} style={{ padding: isMobile ? '4px 12px 10px' : '6px 20px 14px' }}>
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          style={{
            display: 'flex',
            flexDirection: 'column',
            background: dragOver
              ? 'rgba(255,126,103,0.07)'
              : focused
                ? 'rgba(255,255,255,0.06)'
                : 'rgba(255,255,255,0.04)',
            border: `1px solid ${
              dragOver
                ? 'rgba(255,126,103,0.5)'
                : focused
                  ? 'rgba(255,255,255,0.18)'
                  : 'rgba(255,255,255,0.10)'
            }`,
            borderRadius: 18,
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            transition: 'background 200ms ease, border-color 200ms ease, box-shadow 200ms ease',
            boxShadow: dragOver
              ? `0 0 0 2px rgba(255,126,103,0.25), 0 8px 32px rgba(0,0,0,0.3)`
              : focused
                ? `0 0 0 1px rgba(255,126,103,0.15), 0 8px 32px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.06)`
                : `0 4px 20px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.04)`,
            overflow: 'hidden',
          }}
        >
          {/* Image preview strip */}
          {images.length > 0 && (
            <div style={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: 8,
              flexWrap: 'wrap',
              padding: '10px 14px 8px',
              borderBottom: '1px solid rgba(255,255,255,0.06)',
            }}>
              {images.map((img, i) => {
                const roleConf = img.role ? ROLE_CONFIG[img.role] : null;
                return (
                  <div
                    key={i}
                    style={{
                      position: 'relative', flexShrink: 0,
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                      animation: 'messageIn 0.2s ease forwards',
                    }}
                  >
                    {/* Index badge */}
                    <div style={{
                      position: 'absolute', top: -6, left: -1, zIndex: 2,
                      width: 16, height: 16, borderRadius: '50%',
                      background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)',
                      border: '1px solid rgba(255,255,255,0.15)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 8, fontWeight: 700, color: 'rgba(255,255,255,0.7)', lineHeight: 1,
                    }}>
                      {i + 1}
                    </div>
                    <div style={{ position: 'relative', borderRadius: 8, overflow: 'hidden' }}>
                      <img
                        src={img.url}
                        alt={img.name}
                        style={{
                          width: 56, height: 56, objectFit: 'cover',
                          borderRadius: 8, display: 'block',
                          border: roleConf ? `2px solid ${roleConf.border}` : '1px solid rgba(255,255,255,0.1)',
                          transition: 'border-color 150ms ease',
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(i)}
                        title="Remove"
                        style={{
                          position: 'absolute', top: 2, right: 2,
                          width: 16, height: 16, borderRadius: '50%',
                          background: 'rgba(0,0,0,0.65)', color: 'rgba(255,255,255,0.85)',
                          border: 'none', fontSize: 9, fontWeight: 700, cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          padding: 0, lineHeight: 1, transition: 'background 100ms ease',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = '#e53e3e'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(0,0,0,0.65)'; }}
                      >×</button>
                    </div>
                    <button
                      type="button"
                      onClick={() => cycleRole(i)}
                      title={roleConf ? roleConf.title : 'Click to set role'}
                      style={{
                        padding: '2px 6px', borderRadius: 4,
                        border: roleConf ? `1px solid ${roleConf.border}` : '1px solid rgba(255,255,255,0.12)',
                        background: roleConf ? roleConf.bg : 'rgba(255,255,255,0.06)',
                        color: roleConf ? '#fff' : 'rgba(255,255,255,0.35)',
                        fontSize: 8, fontWeight: 700, cursor: 'pointer',
                        letterSpacing: '0.04em', textTransform: 'uppercase',
                        lineHeight: 1.4, whiteSpace: 'nowrap', transition: 'all 150ms ease',
                        minWidth: 32, textAlign: 'center', fontFamily: 'inherit',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.8'; e.currentTarget.style.transform = 'scale(1.05)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'scale(1)'; }}
                    >
                      {roleConf ? roleConf.label : 'Auto'}
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Textarea */}
          <textarea
            ref={textareaRef}
            aria-label="Message input"
            value={input}
            onChange={onInputChange}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            placeholder={disabled ? 'Select a brand to start chatting…' : 'Describe what you want to create…'}
            disabled={disabled}
            rows={1}
            style={{
              flex: 1,
              background: 'transparent',
              border: 'none',
              outline: 'none',
              color: '#FFFFFF',
              fontSize: 15,
              lineHeight: 1.55,
              resize: 'none',
              fontFamily: 'inherit',
              padding: '14px 16px 10px',
              maxHeight: 160,
              caretColor: '#FF7E67',
            }}
          />

          {/* Bottom toolbar */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            padding: '8px 10px 10px',
            gap: 6,
          }}>
            {/* Assets button */}
            <button
              type="button"
              onClick={() => { if (attachActive && openAssetPicker) openAssetPicker(handleAssetsConfirm); }}
              disabled={!attachActive || !openAssetPicker}
              className={attachActive ? 'agent-btn-coral' : ''}
              aria-label="Attach assets"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: isMobile ? '6px 10px' : '6px 13px',
                borderRadius: 20,
                background: 'transparent',
                border: '1px solid rgba(255,255,255,0.12)',
                color: attachActive ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.2)',
                fontSize: 13,
                cursor: attachActive ? 'pointer' : 'default',
                transition: 'all 160ms ease',
                fontFamily: 'inherit',
                fontWeight: 500,
              }}
            >
              {/* Image icon */}
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
              </svg>
              {!isMobile && 'Assets'}
              {images.length > 0 && (
                <span style={{
                  fontSize: 10, fontWeight: 700,
                  background: 'rgba(255,126,103,0.2)',
                  color: '#FF7E67',
                  borderRadius: 10, padding: '0 5px',
                  lineHeight: '16px', minWidth: 16, textAlign: 'center',
                }}>
                  {images.length}
                </span>
              )}
            </button>

            {/* Templates button */}
            {onOpenPresets && !isStreaming && !input?.trim() && (
              <button
                type="button"
                onClick={onOpenPresets}
                aria-label="Open templates"
                className="agent-btn-ghost"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '6px 13px', borderRadius: 20,
                  background: 'transparent',
                  border: '1px solid rgba(255,255,255,0.12)',
                  color: 'rgba(255,255,255,0.6)',
                  fontSize: 12, cursor: 'pointer', fontFamily: 'inherit',
                  transition: 'all 150ms ease',
                }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" />
                </svg>
                {!isMobile && 'Templates'}
              </button>
            )}

            {/* Hidden file input for drag/drop/paste still works */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              style={{ display: 'none' }}
              onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }}
            />

            <div style={{ flex: 1 }} />

            {/* Tri-state button: Send / Stop / Queue */}
            {canStop ? (
              <button
                type="button"
                onClick={onStop}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 7,
                  padding: '7px 18px', borderRadius: 20,
                  background: 'rgba(248,113,113,0.15)',
                  border: '1px solid rgba(248,113,113,0.4)',
                  color: '#f87171', fontSize: 13, fontWeight: 600,
                  cursor: 'pointer', transition: 'all 200ms ease', fontFamily: 'inherit',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(248,113,113,0.25)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(248,113,113,0.15)'; }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                  <rect x="6" y="6" width="12" height="12" rx="2" />
                </svg>
                Stop
              </button>
            ) : canQueue ? (
              <button
                type="button"
                onClick={() => onQueueMessage(input.trim())}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 7,
                  padding: '7px 18px', borderRadius: 20,
                  background: 'linear-gradient(to right, #8B5CF6, #6D28D9)',
                  color: '#fff', border: 'none', fontSize: 13, fontWeight: 600,
                  cursor: 'pointer', transition: 'all 200ms ease', fontFamily: 'inherit',
                  boxShadow: '0 2px 16px rgba(139,92,246,0.4)',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 4px 24px rgba(139,92,246,0.55)'; e.currentTarget.style.transform = 'scale(1.03)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = '0 2px 16px rgba(139,92,246,0.4)'; e.currentTarget.style.transform = 'scale(1)'; }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="17 1 21 5 17 9" />
                  <path d="M3 11V9a4 4 0 0 1 4-4h14" />
                </svg>
                Queue
              </button>
            ) : (
              <button
                type="submit"
                disabled={!canSubmit}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 7,
                  padding: '7px 18px', borderRadius: 20,
                  background: canSubmit ? 'linear-gradient(to right, #FF7E67, #FF4D8D)' : 'rgba(255,255,255,0.06)',
                  color: canSubmit ? '#fff' : 'rgba(255,255,255,0.2)',
                  border: 'none', cursor: canSubmit ? 'pointer' : 'default',
                  fontSize: 13, fontWeight: 600, transition: 'all 200ms ease',
                  boxShadow: canSubmit ? '0 2px 16px rgba(255,126,103,0.4)' : 'none',
                  fontFamily: 'inherit',
                }}
                onMouseEnter={(e) => { if (canSubmit) { e.currentTarget.style.boxShadow = '0 4px 24px rgba(255,126,103,0.55)'; e.currentTarget.style.transform = 'scale(1.03)'; }}}
                onMouseLeave={(e) => { if (canSubmit) { e.currentTarget.style.boxShadow = '0 2px 16px rgba(255,126,103,0.4)'; e.currentTarget.style.transform = 'scale(1)'; }}}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="19" x2="12" y2="5" />
                  <polyline points="5 12 12 5 19 12" />
                </svg>
                Send
              </button>
            )}
          </div>
        </div>

        <div style={{
          textAlign: 'center', marginTop: 5,
          fontSize: 10, color: 'rgba(255,255,255,0.2)', letterSpacing: '0.02em',
        }}>
          AI can make mistakes. Generated content costs are charged automatically.
        </div>
      </form>

    </>
  );
}
